import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const INACTIVITY_LIMIT = 10 * 60 * 1000; // 10 minutes in milliseconds, matches backend USER_SESSION_INACTIVITY_MINUTES
const LAST_ACTIVITY_KEY = 'kiosk_last_activity_at';
const EXPIRY_CHECK_INTERVAL_MS = 30 * 1000;
const PUBLIC_PATHS = new Set(['/', '/login', '/check-eligibility', '/payment-success', '/payment-failure']);

const SessionManager = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const timerRef = useRef(null);
    const expiryCheckRef = useRef(null);

    const isPublicPath = PUBLIC_PATHS.has(location.pathname);

    const readLastActivityAt = useCallback(() => {
        try {
            const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
            return Number.isFinite(value) && value > 0 ? value : 0;
        } catch (_error) {
            return 0;
        }
    }, []);

    const writeLastActivityAt = useCallback(() => {
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
        } catch (_error) {
        }
    }, []);

    const handleLogout = useCallback(() => {
        try {
            localStorage.removeItem(LAST_ACTIVITY_KEY);
        } catch (_error) {
        }
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        sessionStorage.clear();
        navigate('/login', { replace: true });
    }, [navigate]);

    const clearTimers = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (expiryCheckRef.current) clearInterval(expiryCheckRef.current);
        timerRef.current = null;
        expiryCheckRef.current = null;
    }, []);

    const scheduleLogout = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (isPublicPath) return;

        const lastActivityAt = readLastActivityAt();
        const now = Date.now();
        const elapsedMs = lastActivityAt > 0 ? now - lastActivityAt : 0;

        if (elapsedMs >= INACTIVITY_LIMIT) {
            handleLogout();
            return;
        }

        timerRef.current = setTimeout(() => {
            handleLogout();
        }, Math.max(0, INACTIVITY_LIMIT - elapsedMs));
    }, [handleLogout, isPublicPath, readLastActivityAt]);

    useEffect(() => {
        if (isPublicPath) {
            clearTimers();
            return undefined;
        }

        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
            'keydown'
        ];

        const handleInteraction = () => {
            writeLastActivityAt();
            scheduleLogout();
        };
        const handleVisibilityChange = () => {
            if (document.hidden) return;
            writeLastActivityAt();
            scheduleLogout();
        };

        writeLastActivityAt();
        scheduleLogout();
        expiryCheckRef.current = window.setInterval(() => {
            scheduleLogout();
        }, EXPIRY_CHECK_INTERVAL_MS);

        events.forEach(event => {
            window.addEventListener(event, handleInteraction);
        });
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearTimers();
            events.forEach(event => {
                window.removeEventListener(event, handleInteraction);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [clearTimers, isPublicPath, scheduleLogout, writeLastActivityAt]);

    return null;
};

export default SessionManager;
